/**
 * Tally desk. MASTER_PROMPT section 6.19 and decisions A18 and A19.
 *
 * The batch lifecycle is the screen: generate, review the Excel, approve,
 * export the XML, confirm. Excel is the control document accounts sign off;
 * XML is what actually posts.
 *
 * Regeneration is safe and the screen says so plainly. Every voucher carries a
 * source key with a unique constraint behind it, so running generation twice
 * produces an empty batch rather than a duplicated set of books — the failure
 * mode that would otherwise be found a quarter later.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { downloadFile, ops } from "../../lib/api2";
import type {
  BatchRow,
  BillResult,
  GenerateResult,
  MissingMappings,
  Party,
} from "../../lib/types";
import { fmtDate, fmtINR, fmtStatus, thisMonth, toNumber } from "../../lib/format";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

const BATCH_COLUMNS: SheetColumn<BatchRow>[] = [
  { key: "period", label: "Period", type: "date",
    value: (r) => r.period_from,
    format: (r) => `${fmtDate(r.period_from)} – ${fmtDate(r.period_to)}` },
  { key: "stage", label: "Stage", value: (r) => fmtStatus(r.status) },
  { key: "vouchers", label: "Vouchers", type: "number", numeric: true,
    value: (r) => r.voucher_count },
  { key: "debit", label: "Debit", type: "number", numeric: true,
    value: (r) => toNumber(r.total_debit),
    format: (r) => fmtINR(r.total_debit) },
  { key: "credit", label: "Credit", type: "number", numeric: true,
    value: (r) => toNumber(r.total_credit),
    format: (r) => fmtINR(r.total_credit) },
  // A batch that does not balance is the one thing worth filtering for here.
  { key: "balanced", label: "Balanced",
    value: (r) => (r.balanced ? "Balanced" : "Out") },
];

type Tab = "batches" | "mappings" | "billing";

const STAGES = ["DRAFT", "APPROVED", "EXPORTED", "CONFIRMED"];


function monthBounds(month: string): [string, string] {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return [`${month}-01`, `${month}-${String(last).padStart(2, "0")}`];
}

export function TallyDesk() {
  const [tab, setTab] = useState<Tab>("batches");
  // Local month, not the UTC one — see `thisMonth`.
  const [month, setMonth] = useState(thisMonth);
  const [batches, setBatches] = useState<BatchRow[] | null>(null);
  const batchSheet = useSheet(batches ?? [], BATCH_COLUMNS);
  const [generated, setGenerated] = useState<GenerateResult | null>(null);
  const [missing, setMissing] = useState<MissingMappings | null>(null);
  const [bill, setBill] = useState<BillResult | null>(null);
  // Contractors come from masters, so billing reaches whoever is actually on
  // the books rather than whoever was on it when this screen was written.
  const [contractors, setContractors] = useState<Party[]>([]);
  const [contractor, setContractor] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  const loadBatches = useCallback(() => {
    setBatches(null);
    ops
      .batches()
      .then(setBatches)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, []);

  const loadMissing = useCallback(() => {
    setMissing(null);
    ops
      .missingMappings()
      .then(setMissing)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, []);

  useEffect(() => {
    if (tab === "batches") loadBatches();
    if (tab === "mappings") loadMissing();
  }, [tab, loadBatches, loadMissing]);

  useEffect(() => {
    api
      .parties("contractor")
      .then((rows) => {
        setContractors(rows);
        setContractor((current) => current || rows[0]?.code || "");
      })
      .catch(() => undefined);
  }, []);

  async function generate() {
    const [from, to] = monthBounds(month);
    setBusy(true);
    setBanner(null);
    try {
      const result = await ops.generateBatch(from, to);
      setGenerated(result);
      setBanner(
        result.created === 0
          ? {
              tone: "warning",
              text: `Nothing new — all ${result.skipped_already_vouchered} events in this period are already posted. Re-running is safe and produces no duplicates.`,
            }
          : {
              tone: "positive",
              text: `${result.created} vouchers created, ${result.skipped_already_vouchered} skipped as already posted.`,
            }
      );
      loadBatches();
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function act(
    id: number,
    action: "approve" | "confirm",
    label: string
  ) {
    setBusy(true);
    try {
      const result =
        action === "approve"
          ? await ops.approveBatch(id)
          : await ops.confirmBatch(id);
      setBanner({ tone: "positive", text: `Batch ${id} ${label}.` });
      void result;
      loadBatches();
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function download(id: number, kind: "excel" | "xml") {
    try {
      await downloadFile(
        `/api/tally/batches/${id}/${kind}`,
        `tally-batch-${id}.${kind === "excel" ? "xlsx" : "xml"}`
      );
      if (kind === "xml") {
        setBanner({
          tone: "warning",
          text: "XML downloaded and the batch is now marked exported. Import it into a test company first — the tag set shifts between TallyPrime releases.",
        });
        loadBatches();
      }
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    }
  }

  async function raiseBill() {
    const [from, to] = monthBounds(month);
    setBusy(true);
    setBanner(null);
    try {
      const result = await ops.raiseBill({
        contractor_code: contractor,
        period_from: from,
        period_to: to,
      });
      setBill(result);
      setBanner({
        tone: result.warnings.length ? "warning" : "positive",
        text: `${result.bill_no} raised for ${result.contractor}.`,
      });
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
      setBill(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Tally</h2>
          <p className="page__sub">
            Excel to review, XML to post — and regeneration cannot duplicate
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Period</span>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </label>
          <div className="segmented" role="tablist">
            {(
              [
                ["batches", "Batches"],
                ["mappings", "Mappings"],
                ["billing", "Contractor bills"],
              ] as [Tab, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                className={tab === id ? "is-active" : ""}
                onClick={() => setTab(id)}
                role="tab"
                aria-selected={tab === id}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {tab === "batches" && (
        <>
          <section className="card">
            <h3 className="section-title">Generate a batch</h3>
            <p className="section-note">
              Safe to run repeatedly. Events already vouchered are skipped, so a
              second run over the same month produces an empty batch.
            </p>
            <div className="page__footer">
              <button className="btn btn--auto" onClick={generate} disabled={busy}>
                {busy ? <span className="spinner" /> : null}
                Generate for {month}
              </button>
            </div>

            {generated && (
              <div className="kpirow" style={{ marginTop: "var(--sp-4)" }}>
                <div className="kpi">
                  <span className="label">Created</span>
                  <span className="kpi-value num">{generated.created}</span>
                </div>
                <div className="kpi">
                  <span className="label">Already posted</span>
                  <span className="kpi-value num">
                    {generated.skipped_already_vouchered}
                  </span>
                  <span className="kpi__sub">skipped, never duplicated</span>
                </div>
                {Object.entries(generated.by_type).map(([type, count]) => (
                  <div key={type} className="kpi">
                    <span className="label">{type}</span>
                    <span className="kpi-value num">{count}</span>
                  </div>
                ))}
              </div>
            )}

            {generated && generated.unmapped.length > 0 && (
              <div className="banner banner--warning">
                {generated.unmapped.length} names have no Tally mapping. They
                will post under their NPI names — add mappings before approving,
                or accounts will be reconciling by hand.
              </div>
            )}
          </section>

          {!batches ? (
            <div className="state">
              {[0, 1].map((i) => (
                <div key={i} className="skeleton state__bar" />
              ))}
            </div>
          ) : batches.length === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ⇄
              </span>
              <h3>No batches yet</h3>
              <p>Generate one for the period above.</p>
            </div>
          ) : (
            <>
            <SheetToolbar sheet={batchSheet} exportName="voucher-batches" />
            <div className="tablewrap">
              <table className="table">
                <SheetHead sheet={batchSheet} trailing={<th key="actions" />} />
                <tbody>
                  {batchSheet.rows.map((b) => {
                    const stage = STAGES.indexOf(b.status);
                    return (
                      <tr key={b.batch_id}>
                        <td>
                          {fmtDate(b.period_from)} – {fmtDate(b.period_to)}
                        </td>
                        <td>
                          <div className="stages">
                            {STAGES.map((s, i) => (
                              <span
                                key={s}
                                className={`stages__dot${
                                  i <= stage ? " stages__dot--done" : ""
                                }`}
                                title={fmtStatus(s)}
                              />
                            ))}
                            <span className="stages__label">
                              {fmtStatus(b.status)}
                            </span>
                          </div>
                        </td>
                        <td className="num mono">{b.voucher_count}</td>
                        <td className="num mono">{fmtINR(b.total_debit)}</td>
                        <td className="num mono">{fmtINR(b.total_credit)}</td>
                        <td>
                          {b.balanced ? (
                            <span className="pill">✓ yes</span>
                          ) : (
                            <span className="pill pill--warning">▲ no</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "var(--sp-2)" }}>
                            <button
                              className="btn btn--ghost btn--sm"
                              onClick={() => download(b.batch_id, "excel")}
                            >
                              Excel
                            </button>
                            {b.status === "DRAFT" && (
                              <button
                                className="btn btn--ghost btn--sm"
                                onClick={() =>
                                  act(b.batch_id, "approve", "approved")
                                }
                                disabled={busy}
                              >
                                Approve
                              </button>
                            )}
                            {b.status === "APPROVED" && (
                              <button
                                className="btn btn--sm"
                                onClick={() => download(b.batch_id, "xml")}
                              >
                                Export XML
                              </button>
                            )}
                            {b.status === "EXPORTED" && (
                              <button
                                className="btn btn--sm"
                                onClick={() =>
                                  act(b.batch_id, "confirm", "confirmed")
                                }
                                disabled={busy}
                              >
                                Confirm
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}

      {tab === "mappings" && (
        <>
          {!missing ? (
            <div className="state">
              <div className="skeleton state__bar" />
            </div>
          ) : missing.count === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ✓
              </span>
              <h3>Everything is mapped</h3>
              <p>Every item and party has a Tally name.</p>
            </div>
          ) : (
            <>
              <div className="banner banner--warning">
                {missing.count} names have no Tally mapping. An unmapped item
                posts under its NPI name, which leaves the accountant
                reconciling by hand.
              </div>
              <div className="board2">
                {(
                  [
                    ["Items", missing.items],
                    ["Parties", missing.parties],
                  ] as [string, typeof missing.items][]
                ).map(([title, rows]) => (
                  <section key={title} className="card">
                    <h3 className="section-title">
                      {title} ({rows.length})
                    </h3>
                    <div className="tablewrap">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.slice(0, 20).map((r) => (
                            <tr key={r.local_code}>
                              <td className="mono">{r.local_code}</td>
                              <td>{r.name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === "billing" && (
        <>
          <section className="card">
            <h3 className="section-title">Raise a contractor bill</h3>
            <p className="section-note">
              Computed from recorded output at the rate that applied at the
              time. Revising a rate later cannot restate a bill already raised.
            </p>
            <div className="page__footer">
              <label className="inputgroup">
                <span className="label">Contractor</span>
                <select
                  value={contractor}
                  onChange={(e) => setContractor(e.target.value)}
                >
                  {contractors.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <button className="btn btn--auto" onClick={raiseBill} disabled={busy}>
                {busy ? <span className="spinner" /> : null}
                Raise bill for {month}
              </button>
            </div>
          </section>

          {bill && (
            <section className="card">
              <h3 className="section-title">
                {bill.bill_no} · {bill.contractor}
              </h3>
              <div className="kpirow" style={{ marginTop: "var(--sp-4)" }}>
                <div className="kpi">
                  <span className="label">Quantity</span>
                  <span className="kpi-value num">{fmtINR(bill.qty, { abbreviate: false }).replace("₹", "")}</span>
                  <span className="kpi__sub">{bill.entries} entries</span>
                </div>
                <div className="kpi">
                  <span className="label">Gross</span>
                  <span className="kpi-value num">{fmtINR(bill.gross)}</span>
                </div>
                <div className="kpi">
                  <span className="label">GST</span>
                  <span className="kpi-value num">{fmtINR(bill.gst)}</span>
                </div>
                <div className="kpi">
                  <span className="label">TDS</span>
                  <span className="kpi-value num">−{fmtINR(bill.tds)}</span>
                  <span className="kpi__sub">on the service value</span>
                </div>
                <div className="kpi">
                  <span className="label">Net payable</span>
                  <span className="kpi-value num">
                    {fmtINR(bill.net_payable)}
                  </span>
                </div>
              </div>
              {bill.warnings.map((w) => (
                <div key={w} className="banner banner--warning">
                  {w}
                </div>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}
